import ChatRounded from '@mui/icons-material/ChatRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { businessDate } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { useEmailAdmin, useEmailStatus } from './api';

const ASK = '/support?message=' + encodeURIComponent('Hi, I want the Email feature');
const FEATURE =
  'Email invoices, quotations, orders and statements as PDFs from your shop’s own email accounts.';

/**
 * The Email add-on on the Subscription page. Not enabled: dimmed with a lock and a quick way to
 * ask for it. Enabled: its period and grace, how many mail servers the shop may have, and the
 * shop's on/off switch (off hides every Email button but keeps the servers).
 */
export function EmailCard() {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const status = useEmailStatus();
  const admin = useEmailAdmin();
  const plugin = status.data?.plugin;
  if (!plugin) return null;

  if (plugin.state === 'NOT_ENABLED')
    return (
      <Tooltip
        title="Contact us to add Email for a small monthly fee."
        placement="top"
        describeChild
      >
        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              <MailOutlineRounded color="primary" sx={{ fontSize: 40, opacity: 0.45 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="h6">Email</Typography>
                  <Chip size="small" icon={<LockRounded />} label="Add-on" variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {FEATURE}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Contact us to add it for a small monthly fee.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChatRounded />}
                  component={RouterLink}
                  to={ASK}
                  sx={{ mt: 1.5 }}
                >
                  Ask for Email
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Tooltip>
    );

  const chip =
    plugin.state === 'ACTIVE'
      ? { label: 'Active', color: 'success' as const }
      : plugin.state === 'GRACE'
        ? { label: 'In grace', color: 'warning' as const }
        : { label: 'Ended', color: 'error' as const };
  const day = (iso: string | null) => (iso ? businessDate(iso.slice(0, 10), format) : '—');
  const servers = status.data?.servers;

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <MailOutlineRounded color="primary" sx={{ fontSize: 40 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="h6">Email</Typography>
              <Chip size="small" color={chip.color} label={chip.label} variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {plugin.state === 'EXPIRED'
                ? 'Email subscription ended, grace used. Contact PackBytes to renew it.'
                : `Paid through ${day(plugin.paidThrough)} · works until ${day(plugin.graceEndsAt)} with grace`}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {servers
                ? `${String(servers.length)} of ${String(plugin.units)} mail servers set up`
                : `Up to ${String(plugin.units)} mail ${plugin.units === 1 ? 'server' : 'servers'}`}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 1.5, alignItems: { sm: 'center' } }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={plugin.shopEnabled}
                    disabled={!can('email.manage') || admin.settings.isPending}
                    onChange={(e) =>
                      admin.settings.mutate(
                        { shopEnabled: e.target.checked, version: status.data?.version ?? null },
                        { onError: (err) => notify.error(errorMessage(err)) },
                      )
                    }
                  />
                }
                label={plugin.shopEnabled ? 'Shown in the shop' : 'Hidden in the shop'}
              />
              {can('email.manage') && (
                <Button
                  size="small"
                  startIcon={<SettingsRounded />}
                  component={RouterLink}
                  to="/settings?tab=email"
                >
                  Email settings
                </Button>
              )}
              {plugin.state !== 'ACTIVE' && (
                <Button size="small" startIcon={<ChatRounded />} component={RouterLink} to={ASK}>
                  Contact us
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
